import { useState, type Dispatch, type SetStateAction } from 'react';
import { type NavigateFunction } from 'react-router-dom';
import { getRunnableSubSimulations, prepareSimulationForSubmission, submitSimulationSet } from './submitSimulationSet';
import { type GeodataTarget } from 'contexts/GeodataContext';
import { type SimulationState } from 'contexts/SimulationContext';
import { type SimulationGridView, type SimulationProjectData } from './addSimulationShared';
import { getSimulationSets, runSimulationSet } from 'api/simulationSetApi';
import { isShadowSimulationDefined } from '../shadow/shadowResidentialAreas';

type UseAddSimulationSubmitArgs = {
  countsTowardUsage: boolean;
  geodataId: string | null;
  geodataParams: Record<string, unknown>;
  grid: SimulationGridView | null;
  isProjectCreationFlow: boolean;
  navigate: NavigateFunction;
  onShadowNotDefined: () => void;
  project: SimulationProjectData | null;
  setLastAddedItem: Dispatch<SetStateAction<any>>;
  setSimulation: Dispatch<SetStateAction<SimulationState>>;
  showNotification: (message: string) => void;
  simulation: SimulationState;
  simulationSetId: string | null;
};

const useAddSimulationSubmit = ({
  countsTowardUsage,
  geodataId,
  geodataParams,
  grid,
  isProjectCreationFlow,
  navigate,
  onShadowNotDefined,
  project,
  setLastAddedItem,
  setSimulation,
  showNotification,
  simulation,
  simulationSetId,
}: UseAddSimulationSubmitArgs) => {
  const [isRunningFromSummary, setIsRunningFromSummary] = useState(false);
  const [isSavingFromSummary, setIsSavingFromSummary] = useState(false);

  const getAvailableTargets = () =>
    (Array.isArray(geodataParams.targets) ? geodataParams.targets : []) as GeodataTarget[];

  const getPreparedSubmission = () => {
    const preparedSubmission = prepareSimulationForSubmission({
      simulation,
      gridData: grid as { params?: Record<string, unknown> | null },
      lengthMid: geodataParams?.lengthMid,
      availableTargets: getAvailableTargets(),
    });

    setSimulation(preparedSubmission.simulation);
    return preparedSubmission;
  };

  const createSimulationSet = async (runAfterCreate: boolean) => {
    const preparedSubmission = getPreparedSubmission();
    const existingSimulationSets = isProjectCreationFlow
      ? await getSimulationSets({ geodataId: String(geodataId) })
      : [];
    const existingSimulationSetId =
      simulationSetId ??
      (Array.isArray(existingSimulationSets) && existingSimulationSets[0]?.id
        ? String(existingSimulationSets[0].id)
        : null);
    const createdSimulationSet = await submitSimulationSet({
      geodataId: String(geodataId),
      simulationSetId: existingSimulationSetId,
      simulation: preparedSubmission.simulation,
      gridData: grid as { params?: Record<string, unknown> | null },
      lengthMid: geodataParams?.lengthMid,
      resolutionMid: geodataParams?.resolutionMid,
      countsTowardUsage,
      availableTargets: getAvailableTargets(),
    });

    setLastAddedItem(createdSimulationSet);

    if (runAfterCreate) {
      await runSimulationSet(String(createdSimulationSet.id));
    }
  };

  const handleSubmitFromSummary = async (runAfterCreate: boolean, disableRun: boolean) => {
    if (isRunningFromSummary || isSavingFromSummary || !grid?.id || !geodataId || (runAfterCreate && disableRun)) {
      return;
    }
    if (!isShadowSimulationDefined(simulation)) {
      onShadowNotDefined();
      return;
    }

    try {
      if (runAfterCreate) {
        setIsRunningFromSummary(true);
      } else {
        setIsSavingFromSummary(true);
      }

      const preparedSubmission = getPreparedSubmission();
      const runnableSubSimulations = getRunnableSubSimulations(
        preparedSubmission.simulation,
        grid as { params?: Record<string, unknown> | null },
        geodataParams?.lengthMid,
        getAvailableTargets()
      );

      if (runnableSubSimulations.length > 0) {
        await createSimulationSet(runAfterCreate);
      }

      const projectId = grid?.projectId ?? project?.id;
      window.location.assign(projectId ? `/projects/${projectId}` : '/dashboard');
    } catch (error) {
      console.error(`Error ${runAfterCreate ? 'running' : 'saving'} project creation flow:`, error);
      showNotification(
        runAfterCreate
          ? 'Unable to start the run. Check the data and try again.'
          : 'Unable to save the simulation set. Check the data and try again.'
      );
      setIsRunningFromSummary(false);
      setIsSavingFromSummary(false);
    }
  };

  const handleCreateSimulationSet = async (runAfterCreate = false) => {
    if (isRunningFromSummary || !grid?.id || !geodataId) {
      return;
    }
    if (!isShadowSimulationDefined(simulation)) {
      onShadowNotDefined();
      return;
    }

    try {
      setIsRunningFromSummary(true);
      await createSimulationSet(runAfterCreate);

      const projectId = grid?.projectId ?? project?.id;
      navigate(projectId ? `/projects/${projectId}` : '/dashboard');
    } catch (error) {
      console.error(`Error ${runAfterCreate ? 'running' : 'creating'} simulation set from standalone flow:`, error);
      showNotification(
        runAfterCreate
          ? 'Unable to create and run the simulation set. Check the data and try again.'
          : 'Unable to create the simulation set. Check the data and try again.'
      );
      setIsRunningFromSummary(false);
    }
  };

  return {
    handleCreateSimulationSet,
    handleSubmitFromSummary,
    isRunningFromSummary,
    isSavingFromSummary,
  };
};

export default useAddSimulationSubmit;
